import {useCallback, useEffect, useRef, useState} from 'react';
import {AppState} from 'react-native';
import {api} from '../../../../../services/api';
import use3DS from '../../../../../utils/payments/3ds';
import {UserCreditCardsDTO} from '../../../../../dtos/UserCreditCardsDTO';
import {CardGatewayCallbacks, ChargeCardFn} from './types';
import {useCardProcessingPolling} from '../hooks/useCardProcessingPolling';

type Options = CardGatewayCallbacks & {
  chargeCard: ChargeCardFn;
  selectedCard: UserCreditCardsDTO | undefined;
};

export function useStripeGateway({
  chargeCard,
  selectedCard,
  onSuccess,
  onError,
}: Options) {
  const {createStripeToken} = use3DS();

  const [isPaying, setIsPaying] = useState(false);
  const [webviewUri, setWebviewUri] = useState('');
  const [intentId, setIntentId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [loaded3ds, setLoaded3ds] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const {startPolling: startCardPolling, isPolling, clearPolling: clearCardPolling} =
    useCardProcessingPolling({onSuccess, onError});

  const clearStripePolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearPolling = useCallback(() => {
    clearStripePolling();
    clearCardPolling();
  }, [clearStripePolling, clearCardPolling]);

  const reset = useCallback(() => {
    clearPolling();
    setIntentId('');
    setWebviewUri('');
    setPaymentStatus('');
    setLoaded3ds(false);
  }, [clearPolling]);

  const handleStatus = useCallback(
    async (status: string, paymentIntentId: string) => {
      setPaymentStatus(status);

      if (status === 'requires_action') {
        return;
      }

      if (status === 'processing') {
        setTimeout(async () => {
          try {
            const {data} = await api.get('/statusIntentStripe3ds', {
              params: {payment_intent_id: paymentIntentId},
            });
            handleStatus(data.status, paymentIntentId);
          } catch {
            clearStripePolling();
            onError();
          }
        }, 1000);
        return;
      }

      if (status === 'succeeded') {
        setIsPaying(true);
        reset();
        onSuccess();
      } else {
        reset();
        onError();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearStripePolling, onSuccess, onError, reset],
  );

  const startPolling = useCallback(
    (paymentIntentId: string) => {
      clearStripePolling();
      intervalRef.current = setInterval(async () => {
        try {
          const {data} = await api.get('/statusIntentStripe3ds', {
            params: {payment_intent_id: paymentIntentId},
          });
          handleStatus(data.status, paymentIntentId);
        } catch {
          clearStripePolling();
          onError();
        }
      }, 2000);
    },
    [clearStripePolling, handleStatus, onError],
  );

  useEffect(() => {
    if (intentId && loaded3ds) {
      startPolling(intentId);
    } else {
      clearStripePolling();
    }
    return () => clearStripePolling();
  }, [intentId, loaded3ds, startPolling, clearStripePolling]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', nextState => {
      if (nextState !== 'active') {
        clearStripePolling();
      } else if (intentId && loaded3ds) {
        startPolling(intentId);
      }
    });
    return () => sub.remove();
  }, [intentId, loaded3ds, startPolling, clearStripePolling]);

  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  const pay = useCallback(
    async (cvc: string) => {
      if (!selectedCard) {
        return;
      }
      setIsPaying(true);
      try {
        const [month, year] = selectedCard.expiration.split('/');
        const expMonth = parseInt(month, 10);
        const expYear = parseInt(year.slice(-2), 10);

        const {data: token} = await createStripeToken({
          cardNumber: selectedCard.number,
          cvc,
          expMonth,
          expYear,
        });

        if (!token || token.error) {
          onError();
          return;
        }

        const data = await chargeCard({
          cvc,
          tokenId: token.id,
          three_ds_provider: 'stripe',
          is3ds: true,
        });

        if (data?.processando) {
          startCardPolling({ compra_id: data.compra_id });
          return;
        }

        const intent = data?.payment_intent_object;
        if (intent?.next_action?.type === 'redirect_to_url') {
          setWebviewUri(intent.next_action.redirect_to_url.url);
          setIntentId(intent.id);
        } else {
          onSuccess();
        }
      } catch {
        onError();
      } finally {
        setIsPaying(false);
      }
    },
    [chargeCard, createStripeToken, selectedCard, onSuccess, onError, startCardPolling],
  );

  return {
    pay,
    isPaying,
    isPolling,
    webviewUri,
    paymentStatus,
    onWebviewLoaded: () => setLoaded3ds(true),
    onWebviewCancel: reset,
    clearPolling,
  };
}
